<template>
    <BaseCard as="article" padding="lg" class="magic-card" :class="{ 'is-banner': presentation === 'banner' }" :aria-labelledby="headingId" :aria-busy="pending">
        <div class="masthead">
            <SourceChip v-if="presentation === 'banner'" source="nadia" size="xs">{{ t("coachingSetup.home.label") }}</SourceChip>
            <div v-else class="progress-ring">
                <ProgressCircle
                    :progress="progress"
                    content=""
                    gradient-from="var(--color-accent)"
                    gradient-to="var(--color-progress-track)"
                    :aria-label="t('coachingSetup.home.progressLabel')"
                    :aria-valuetext="t('components.progressCircle.complete', { percent: formatNumber(progress / 100, 'percent') })" />
                <i class="bi bi-stars ring-sparkle" aria-hidden="true"></i>
            </div>
            <BaseButton theme="tertiary" icon-only size="sm" :aria-label="t('coachingSetup.home.dismiss')" :disabled="pending" @click="emit('dismiss', $event)">
                <i class="bi bi-x-lg" aria-hidden="true"></i>
            </BaseButton>
        </div>
        <h3 :id="headingId" class="headline">{{ hasStarted ? t("coachingSetup.home.progressedTitle") : t("coachingSetup.home.newTitle") }}</h3>
        <p class="description">{{ description }}</p>
        <div v-if="presentation === 'banner'" class="progress">
            <p class="body-small text-ink-muted">{{ t("coachingSetup.home.progressLabel") }}</p>
            <BaseProgressBar :value="card.completed_count" :max="card.total" color="var(--color-accent)" :aria-label="t('coachingSetup.home.progressLabel')" />
        </div>
        <div class="actions">
            <p v-if="presentation === 'banner'" class="body-small text-ink-muted">{{ t("coachingSetup.home.duration") }}</p>
            <BaseButton theme="primary" :disabled="pending" :aria-busy="pending" class="continue" @click="emit('continue')">
                {{ hasStarted ? t("coachingSetup.home.finish") : t("coachingSetup.home.start") }}
            </BaseButton>
        </div>
        <p role="status" class="body-small text-ink empty:sr-only">
            <span v-if="error">{{ error }}</span>
        </p>
    </BaseCard>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import ProgressCircle from "~vue/components/ProgressCircle.vue";
import SourceChip from "~vue/components/SourceChip.vue";
import type { SetupHomeCard } from "~vue/ProfileSetup/types";
import { formatNumber } from "~vue/utils/formatNumber";
import { computed, useId } from "vue";
import { useI18n } from "vue-i18n";

const {
    card,
    presentation = "card",
    pending = false,
    error = null,
} = defineProps<{
    card: SetupHomeCard;
    presentation?: "card" | "banner";
    pending?: boolean;
    error?: string | null;
}>();
const emit = defineEmits<{ continue: []; dismiss: [event: MouseEvent] }>();
const { t } = useI18n();
const headingId = useId();
const hasStarted = computed(() => card.steps.some((step) => step.id !== "identity" && step.done));
const progress = computed(() => (card.total > 0 ? (card.completed_count / card.total) * 100 : 0));
const description = computed(() => {
    if (presentation === "banner") {
        return hasStarted.value ? t("coachingSetup.home.progressedBannerDescription") : t("coachingSetup.home.newBannerDescription");
    }
    return hasStarted.value ? t("coachingSetup.home.progressedCardDescription") : t("coachingSetup.home.newCardDescription");
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .magic-card {
        @apply flex min-w-0 flex-col gap-4;
    }
    .masthead {
        @apply flex items-start justify-between gap-4;
    }
    .progress-ring {
        @apply relative size-20 shrink-0;
    }
    .ring-sparkle {
        @apply pointer-events-none absolute inset-0 flex items-center justify-center text-3xl text-accent;
    }
    .headline {
        @apply title-body-bold text-pretty text-ink;
    }
    .description {
        @apply body text-ink-muted;
    }
    .progress {
        @apply flex flex-col gap-2;
    }
    .actions {
        @apply mt-auto flex flex-wrap items-center justify-between gap-4;
    }
    .continue {
        @apply w-full;
    }
    .is-banner .headline {
        @apply display-small-serif border-b border-border pb-4;
    }
    .is-banner .description {
        @apply text-ink;
    }
    .is-banner .continue {
        @apply sm:ml-auto sm:w-auto;
    }
}
</style>
